import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const EmailConfirmed = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const checkConfirmation = async () => {
      try {
        // Supabase maneja la confirmación automáticamente via URL hash/params
        // Verificar si hay una sesión activa con email confirmado
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user?.email_confirmed_at) {
          console.log("✅ Email confirmado, usuario autenticado");
          setStatus('success');
          setMessage('¡Tu email ha sido confirmado exitosamente!');
          
          // Redirigir al home después de 3 segundos
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 3000);
          return;
        }

        // Verificar si hay error en los parámetros de URL (hash o query)
        const hashParams = new URLSearchParams(window.location.hash.slice(1));
        const queryParams = new URLSearchParams(window.location.search);
        
        const errorDescription = hashParams.get('error_description') || queryParams.get('error_description');
        const errorCode = hashParams.get('error_code') || queryParams.get('error_code');
        
        if (errorDescription || errorCode) {
          console.error("❌ Error en confirmación:", { errorDescription, errorCode });
          setStatus('error');
          setMessage(errorDescription || 'Error al confirmar el email');
          return;
        }

        // Si llegamos aquí sin sesión pero sin error, el email fue confirmado
        // pero el usuario necesita iniciar sesión
        console.log("ℹ️ Email confirmado, redirigiendo a login");
        setStatus('success');
        setMessage('¡Tu email ha sido confirmado! Ya puedes iniciar sesión.');
        
        // Redirigir al login después de 3 segundos
        setTimeout(() => {
          navigate('/auth', { replace: true });
        }, 3000);

      } catch (error: any) {
        console.error('Error checking confirmation:', error);
        setStatus('error');
        setMessage(error.message || 'Error al verificar la confirmación');
      }
    };

    checkConfirmation();
  }, [navigate]);

  const handleGoToLogin = () => {
    navigate('/auth', { replace: true });
  };

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="h-16 w-16 text-white mx-auto animate-spin" />
              <h1 className="text-2xl font-bold text-white">
                Verificando tu email...
              </h1>
              <p className="text-white/80">
                Por favor espera mientras procesamos tu confirmación
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 text-green-400 mx-auto" />
              <h1 className="text-2xl font-bold text-white">
                ¡Email Confirmado! ✨
              </h1>
              <p className="text-white/80">
                {message}
              </p>
              <p className="text-white/60 text-sm">
                Redirigiendo en unos segundos...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 text-red-400 mx-auto" />
              <h1 className="text-2xl font-bold text-white">
                Error de Confirmación
              </h1>
              <p className="text-white/80">
                {message}
              </p>
            </>
          )}
        </div>

        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6">
          {status === 'success' && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-white/90 text-sm mb-4">
                  ¡Bienvenido a <span style={{ fontFamily: "'Zen Dots', cursive" }}>Clubbers</span>! 🏋️‍♂️
                </p>
                <p className="text-white/70 text-xs">
                  Ya puedes disfrutar de todas las funcionalidades
                </p>
              </div>
              <Button
                onClick={handleGoHome}
                className="w-full bg-white text-primary hover:bg-white/90"
              >
                Ir al Inicio
              </Button>
            </div>
          )}

          {status === 'error' && (
            <Button
              onClick={handleGoToLogin}
              className="w-full bg-white text-primary hover:bg-white/90"
            >
              Ir al Login
            </Button>
          )}
        </div>

        {status === 'loading' && (
          <div className="text-center">
            <p className="text-white/60 text-xs">
              Este proceso puede tardar unos segundos
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailConfirmed;
